/**
 * Goods Parsing Review Interface
 * Interactive interface for reviewing and correcting goods description parsing
 */

import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Select, 
  Button, 
  Input, 
  Modal, 
  Form, 
  Tag, 
  Space, 
  Pagination, 
  Alert, 
  Statistic, 
  Row, 
  Col,
  message,
  Tooltip,
  Progress
} from 'antd';
import { 
  CheckCircleOutlined, 
  ExclamationCircleOutlined, 
  CloseCircleOutlined,
  EditOutlined,
  ReloadOutlined,
  SaveOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

interface GoodsParseResult {
  original: string;
  variety?: string;
  grade?: string;
  size?: string;
  spec?: string;
  confidence: number;
  count: number;
}

interface GoodsAnalysisResponse {
  results: GoodsParseResult[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
  summary: {
    total_descriptions: number;
    high_confidence: number;
    medium_confidence: number;
    low_confidence: number;
  };
}

interface Commodity {
  id: number;
  commodity: string;
  variation?: string;
  hs_code?: string;
}

const { Option } = Select;

const GoodsReview: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);
  const [confidenceFilter, setConfidenceFilter] = useState<string>('all');
  const [commodityFilter, setCommodityFilter] = useState<string>('');
  const [editingItem, setEditingItem] = useState<GoodsParseResult | null>(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [corrections, setCorrections] = useState<Map<string, Partial<GoodsParseResult>>>(new Map());
  
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  // Fetch goods analysis results
  const { data: analysisData, isLoading: analysisLoading, error: analysisError } = useQuery({
    queryKey: ['goods-analysis', currentPage, pageSize, confidenceFilter, commodityFilter],
    queryFn: async (): Promise<GoodsAnalysisResponse> => {
      const params = new URLSearchParams({
        limit: pageSize.toString(),
        offset: ((currentPage - 1) * pageSize).toString(),
        confidence: confidenceFilter,
      });
      
      if (commodityFilter) {
        params.append('commodity', commodityFilter);
      }
      
      const response = await axios.get(`/api/v1/goods-review/analysis?${params.toString()}`);
      return response.data;
    },
  });

  // Fetch available commodities
  const { data: commoditiesData } = useQuery({
    queryKey: ['commodities'],
    queryFn: async (): Promise<{ commodities: Commodity[] }> => {
      const response = await axios.get('/api/v1/goods-review/commodities');
      return response.data;
    },
  });

  // Re-parse goods description
  const reParseMutation = useMutation({
    mutationFn: async (params: { description: string; commodity?: string; hints?: any }) => {
      const response = await axios.post('/api/v1/goods-review/reparse', params);
      return response.data;
    },
    onSuccess: (data) => {
      message.success('Description re-parsed successfully');
      form.setFieldsValue({
        variety: data.parsed.variety || '',
        grade: data.parsed.grade || '',
        size: data.parsed.size || '',
        spec: data.parsed.spec || '',
      });
    },
    onError: () => {
      message.error('Failed to re-parse description');
    },
  });

  // Save corrections
  const saveCorrectionsMutation = useMutation({
    mutationFn: async (correctionsArray: any[]) => {
      const response = await axios.post('/api/v1/goods-review/corrections', {
        corrections: correctionsArray,
      });
      return response.data;
    },
    onSuccess: (data) => {
      message.success(`Saved ${data.saved} corrections successfully`);
      setCorrections(new Map());
    },
    onError: () => {
      message.error('Failed to save corrections');
    },
  });

  // Regenerate analysis
  const regenerateMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post('/api/v1/goods-review/regenerate');
      return response.data;
    },
    onSuccess: () => {
      message.success('Analysis regenerated successfully');
      queryClient.invalidateQueries({ queryKey: ['goods-analysis'] });
    },
    onError: () => {
      message.error('Failed to regenerate analysis');
    },
  });

  const getConfidenceColor = (confidence: number): string => {
    if (confidence >= 80) return '#52c41a';
    if (confidence >= 40) return '#faad14';
    return '#ff4d4f';
  };

  const getConfidenceIcon = (confidence: number) => {
    if (confidence >= 80) return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    if (confidence >= 40) return <ExclamationCircleOutlined style={{ color: '#faad14' }} />;
    return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
  };

  const handleEdit = (record: GoodsParseResult) => {
    setEditingItem(record);
    form.setFieldsValue({
      original: record.original,
      variety: record.variety || '',
      grade: record.grade || '',
      size: record.size || '',
      spec: record.spec || '',
    });
    setEditModalVisible(true);
  };

  const handleSaveEdit = () => {
    form.validateFields().then((values) => {
      if (!editingItem) return;
      
      const newCorrections = new Map(corrections);
      newCorrections.set(editingItem.original, {
        variety: values.variety || undefined,
        grade: values.grade || undefined,
        size: values.size || undefined,
        spec: values.spec || undefined,
        confidence: 95, // Manual correction gets high confidence
      });
      
      setCorrections(newCorrections);
      setEditModalVisible(false);
      setEditingItem(null);
      message.success('Correction saved locally');
    });
  };

  const handleBulkSave = () => {
    const correctionsArray = Array.from(corrections.entries()).map(([original, corrected]) => ({
      original,
      corrections: corrected,
    }));
    
    saveCorrectionsMutation.mutate(correctionsArray);
  };

  const handleReparse = () => {
    if (!editingItem) return;
    
    const hints = form.getFieldsValue();
    reParseMutation.mutate({
      description: editingItem.original,
      commodity: 'almond', // Default for now
      hints: Object.keys(hints).reduce((acc, key) => {
        if (hints[key] && hints[key].trim()) {
          acc[key] = hints[key].trim();
        }
        return acc;
      }, {} as any),
    });
  };

  const columns = [
    {
      title: 'Original Description',
      dataIndex: 'original',
      key: 'original',
      width: '30%',
      render: (text: string, record: GoodsParseResult) => (
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            Used {record.count} times
          </div>
        </div>
      ),
    },
    {
      title: 'Parsed Results',
      key: 'parsed',
      width: '40%',
      render: (_: any, record: GoodsParseResult) => {
        const correction = corrections.get(record.original);
        const displayData = correction || record;
        
        return (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {displayData.variety && (
              <Tag color="blue">Variety: {displayData.variety}</Tag>
            )}
            {displayData.grade && (
              <Tag color="green">Grade: {displayData.grade}</Tag>
            )}
            {displayData.size && (
              <Tag color="orange">Size: {displayData.size}</Tag>
            )}
            {displayData.spec && (
              <Tag color="purple">Spec: {displayData.spec}</Tag>
            )}
            {correction && (
              <Tag color="gold">Manually Corrected</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Confidence',
      dataIndex: 'confidence',
      key: 'confidence',
      width: '15%',
      sorter: (a: GoodsParseResult, b: GoodsParseResult) => a.confidence - b.confidence,
      render: (confidence: number) => (
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: 4 }}>
            {getConfidenceIcon(confidence)}
          </div>
          <Progress
            percent={confidence}
            size="small"
            strokeColor={getConfidenceColor(confidence)}
            showInfo={false}
          />
          <div style={{ fontSize: '12px', marginTop: 2 }}>
            {confidence}%
          </div>
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '15%',
      render: (_: any, record: GoodsParseResult) => (
        <Space>
          <Tooltip title="Edit parsing results">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (analysisError) {
    return (
      <Alert
        message="Error Loading Goods Analysis"
        description="Failed to load goods parsing analysis. Please ensure the migration system is running."
        type="error"
        showIcon
      />
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: '24px' }}>
          <h1>Goods Description Parsing Review</h1>
          <p>Review and correct automated parsing of legacy goods descriptions before migration.</p>
        </div>

        {/* Summary Statistics */}
        {analysisData?.summary && (
          <Row gutter={16} style={{ marginBottom: '24px' }}>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Total Descriptions"
                  value={analysisData.summary.total_descriptions}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="High Confidence (80%+)"
                  value={analysisData.summary.high_confidence}
                  valueStyle={{ color: '#52c41a' }}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Medium Confidence (40-79%)"
                  value={analysisData.summary.medium_confidence}
                  valueStyle={{ color: '#faad14' }}
                  prefix={<ExclamationCircleOutlined />}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Low Confidence (<40%)"
                  value={analysisData.summary.low_confidence}
                  valueStyle={{ color: '#ff4d4f' }}
                  prefix={<CloseCircleOutlined />}
                />
              </Card>
            </Col>
          </Row>
        )}

        {/* Filters and Actions */}
        <Row gutter={16} style={{ marginBottom: '16px' }}>
          <Col span={6}>
            <Select
              placeholder="Filter by confidence"
              value={confidenceFilter}
              onChange={setConfidenceFilter}
              style={{ width: '100%' }}
            >
              <Option value="all">All Confidence Levels</Option>
              <Option value="high">High Confidence (80%+)</Option>
              <Option value="medium">Medium Confidence (40-79%)</Option>
              <Option value="low">Low Confidence (&lt;40%)</Option>
            </Select>
          </Col>
          <Col span={6}>
            <Select
              placeholder="Filter by commodity"
              value={commodityFilter}
              onChange={setCommodityFilter}
              style={{ width: '100%' }}
              allowClear
            >
              {commoditiesData?.commodities.map((commodity) => (
                <Option key={commodity.id} value={commodity.commodity}>
                  {commodity.commodity}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={12}>
            <Space style={{ float: 'right' }}>
              {corrections.size > 0 && (
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleBulkSave}
                  loading={saveCorrectionsMutation.isPending}
                >
                  Save {corrections.size} Corrections
                </Button>
              )}
              <Button
                icon={<ReloadOutlined />}
                onClick={() => regenerateMutation.mutate()}
                loading={regenerateMutation.isPending}
              >
                Regenerate Analysis
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Results Table */}
        <Table
          columns={columns}
          dataSource={analysisData?.results || []}
          loading={analysisLoading}
          rowKey="original"
          pagination={false}
          scroll={{ y: 600 }}
          rowClassName={(record) => 
            corrections.has(record.original) ? 'corrected-row' : ''
          }
        />

        {/* Pagination */}
        {analysisData?.pagination && (
          <div style={{ marginTop: '16px', textAlign: 'center' }}>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={analysisData.pagination.total}
              onChange={(page) => setCurrentPage(page)}
              showSizeChanger={false}
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} of ${total} descriptions`
              }
            />
          </div>
        )}
      </Card>

      {/* Edit Modal */}
      <Modal
        title="Edit Goods Description Parsing"
        open={editModalVisible}
        onOk={handleSaveEdit}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingItem(null);
        }}
        width={800}
        footer={[
          <Button key="reparse" onClick={handleReparse} loading={reParseMutation.isPending}>
            Re-parse with AI
          </Button>,
          <Button key="cancel" onClick={() => setEditModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="save" type="primary" onClick={handleSaveEdit}>
            Save Correction
          </Button>,
        ]}
      >
        {editingItem && (
          <Form form={form} layout="vertical">
            <Form.Item label="Original Description">
              <Input.TextArea value={editingItem.original} disabled rows={2} />
            </Form.Item>
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="variety" label="Variety">
                  <Input placeholder="e.g., Nonpareil, Butte/Padre" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="grade" label="Grade">
                  <Input placeholder="e.g., U.S. Extra No. 1, SSR" />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="size" label="Size">
                  <Input placeholder="e.g., 25/27, 30/32" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="spec" label="Specification">
                  <Input placeholder="e.g., J-spec, Low FM" />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        )}
      </Modal>

      <style>{`
        .corrected-row {
          background-color: #f6ffed !important;
        }
      `}</style>
    </div>
  );
};

export default GoodsReview;